import React, { useState, useEffect } from "react";
import { isEmpty, range } from "ramda";
import { Container, Button, Typography } from "@mui/material";
import PropTypes from "prop-types";

import { useUsers, useFaces, useFaceOrg, useCalendar, useLoading } from "hooks";
import { trackCalendarHit } from "tracking/google/analytics";
import UserPresenter from "presenters/UserPresenter";
import Placeholder from "components/Placeholder";
import { ENTRY_POINTS } from "enums";

import DisplayPerson from "./components/DisplayPerson/DisplayPerson";
import AlgoliaCalendar from "./components/AlgoliaCalendar/AlgoliaCalendar";
import useStyles from "./useStyles";

const UpcomingDates = ({ isDateTypeDisplay, peopleLimit }) => {
  const classes = useStyles();
  const { currentUser } = useUsers();
  const { openModal: openFacesModal, resetAdded } = useFaces();
  const { celebrations, loadCelebrations, peopleParams } = useCalendar();
  const { func: loadPeopleWithLoader, isFinished: isPeopleLoaded } = useLoading(loadCelebrations);
  const [ctaCount, setCtaCount] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalArgs, setModalArgs] = useState({});
  const { setEntryPoint } = useFaceOrg();

  useEffect(() => {
    return () => {
      resetAdded();
    };
  }, []);

  const handleAddBirthdays = () => openFacesModal();
  const handlePersonClick = (args) => {
    trackCalendarHit(args.dateType);
    setShowModal(true);
    setModalArgs({ ...args });
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setModalArgs({});
  };

  const renderComponent = () => {
    if (isEmpty(celebrations)) {
      return (
        <div className={classes.blankView}>
          <Typography variant="h6">Hey, you have no upcoming occasions!</Typography>
          <Typography variant="h6">Add dates to unlock the fun!</Typography>

          <Button
            data-testid="upcomingdates-buttonadd"
            variant="contained"
            color="primary"
            className={classes.addBirthdaysBtn}
            onClick={() => {
              handleAddBirthdays();
              setEntryPoint(ENTRY_POINTS.CALENDAR);
            }}
          >
            Add occasions
          </Button>
        </div>
      );
    }

    return (
      <div className={classes.people}>
        {!isEmpty(celebrations) &&
          celebrations.slice(0, peopleLimit).map(({ person, key, dateType, dateToCelebrate }) => {
            return (
              <DisplayPerson
                person={person}
                dateType={dateType}
                dateToCelebrate={dateToCelebrate}
                key={key}
                onClick={handlePersonClick}
                isDateTypeDisplay={isDateTypeDisplay}
              />
            );
          })}
        {ctaCount.map((index) => (
          <DisplayPerson
            isCTA
            key={index}
            onClick={() => {
              handleAddBirthdays();
              setEntryPoint(ENTRY_POINTS.CALENDAR);
            }}
            isDateTypeDisplay={isDateTypeDisplay}
          />
        ))}
      </div>
    );
  };

  useEffect(() => {
    const ctaLength = range(0, 6 - celebrations.length);
    setCtaCount(ctaLength);
  }, [celebrations]);

  useEffect(() => {
    if (UserPresenter.isRegistered(currentUser)) {
      loadPeopleWithLoader(peopleParams(true));
    }
  }, [currentUser]);

  if (UserPresenter.notRegistered(currentUser)) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Container maxWidth="lg">
        <Typography variant="h5" className={classes.title}>
          Upcoming Occasions
        </Typography>
        {!isPeopleLoaded ? <Placeholder variant="rect" /> : renderComponent()}
        {showModal && <AlgoliaCalendar {...modalArgs} onClose={handleCloseModal} />}
      </Container>
    </div>
  );
};

UpcomingDates.propTypes = {
  isDateTypeDisplay: PropTypes.bool,
  peopleLimit: PropTypes.number,
};

UpcomingDates.defaultProps = {
  isDateTypeDisplay: true,
  peopleLimit: 6,
};

export default React.memo(UpcomingDates);
